import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BypassErrorService } from '@uoa/error-pages';

import { environment } from 'src/environments/environment';
import { User } from '../../domain/user';
import { Pigeon, PostfeedEntry } from '../../domain/pigeon';

// Error codes that write operations handle themselves — bypass the global error page.
const BYPASS_CODES = [400, 409, 422, 500, 502, 503];

// 409 response body for add and edit pigeon — callers catch HttpErrorResponse
// with status 409 and cast error to CageConflict.
export { CageConflict } from '../../domain/cage-conflict';

export interface UpsertUserRequest {
    upi: string;
    firstName?: string;
    lastName?: string;
    isActive?: boolean;
    role?: 'admin' | 'feeder' | 'guest';
    mail?: string;
    note?: string;
}

export interface AddPigeonRequest {
    tag: string;
    isQuarantined: boolean;
    exptNumber: string;
    birdSequence: string;
    comment: string;
    RFID: string;
}

export interface EditPigeonRequest {
    tag: string;
    isQuarantined: boolean;
    exptNumber: string;
    birdSequence: string;
    comment: string;
    targetWeight: number;
    status: string;
    postfeed: PostfeedEntry[];
    RFID: string;
}

export interface WeighAndFeedRequest {
    status: string;
    targetWeight: number;
    comment: string;
    postfeed: PostfeedEntry[];
    currentWeight: number;
    currentFeed: number;
    currentEggs: number;
    date: string; // "YYYY-M-D" local date from the browser
}

export interface GetPigeonsParams {
    showInactive?: boolean;
    simpleSort?: boolean;
    selectedDate?: string; // "YYYY-M-D"
}

@Injectable({ providedIn: 'root' })
export class ApiService {

    private readonly http = inject(HttpClient);
    private readonly bypassErrors = inject(BypassErrorService);
    private readonly baseUrl: string = (environment as any).apiBaseUrl;

    private bypass(url: string): void {
        this.bypassErrors.bypassError(url, BYPASS_CODES);
    }

    // Users

    getUsers(): Observable<User[]> {
        return this.http.get<User[]>(`${this.baseUrl}/users`);
    }

    upsertUser(request: UpsertUserRequest): Observable<User> {
        const url = `${this.baseUrl}/users`;
        this.bypass(url);
        return this.http.post<User>(url, request);
    }

    deactivateUser(upi: string): Observable<void> {
        const url = `${this.baseUrl}/users/${upi}/deactivate`;
        this.bypass(url);
        return this.http.patch<void>(url, {});
    }

    getUserByUpi(upi: string): Observable<User> {
        return this.http.get<User>(`${this.baseUrl}/users/${upi}`);
    }

    // Pigeons

    getPigeons(params?: GetPigeonsParams): Observable<Pigeon[]> {
        let httpParams = new HttpParams();
        if (params?.showInactive !== undefined) {
            httpParams = httpParams.set('showInactive', String(params.showInactive));
        }
        if (params?.simpleSort !== undefined) {
            httpParams = httpParams.set('simpleSort', String(params.simpleSort));
        }
        if (params?.selectedDate) {
            httpParams = httpParams.set('selectedDate', params.selectedDate);
        }
        return this.http.get<Pigeon[]>(`${this.baseUrl}/pigeons`, { params: httpParams });
    }

    getPigeonById(id: string): Observable<Pigeon> {
        return this.http.get<Pigeon>(`${this.baseUrl}/pigeons/${id}`);
    }

    // On cage conflict the backend returns HTTP 409 with body { conflictingPigeonId, message }.
    // Callers catch HttpErrorResponse with status 409 and read err.error as CageConflict.
    addPigeon(request: AddPigeonRequest): Observable<Pigeon> {
        const url = `${this.baseUrl}/pigeons`;
        this.bypass(url);
        return this.http.post<Pigeon>(url, request);
    }

    editPigeon(id: string, request: EditPigeonRequest): Observable<void> {
        const url = `${this.baseUrl}/pigeons/${id}`;
        this.bypass(url);
        return this.http.put<void>(url, request);
    }

    weighAndFeed(id: string, request: WeighAndFeedRequest): Observable<void> {
        const url = `${this.baseUrl}/pigeons/${id}/weigh-feed`;
        this.bypass(url);
        return this.http.post<void>(url, request);
    }

    swapPigeons(pigeon1Id: string, pigeon2Id: string): Observable<void> {
        const url = `${this.baseUrl}/pigeons/swap`;
        this.bypass(url);
        return this.http.post<void>(url, { pigeon1Id, pigeon2Id });
    }
}
